import { z } from 'zod';

// Shared zod primitives for one physical address block (line1/line2/[line3]/country/region/postal
// code) — reused by the checkout billing form, the checkout shipping form, and the profile
// shipping section. The billing dialog's own createBillingDetailsSchema (billingDetailsSchema.js)
// keeps its own composition since it predates this extraction and carries extra fields (company,
// contact, email, tax) with slightly different rules; these primitives exist so new address forms
// don't each re-derive the same length bounds.
export const addressLine1Schema = z.string().max(255, 'Address line 1 must not exceed 255 characters.').optional();
export const addressLine2Schema = z.string().max(255, 'Address line 2 must not exceed 255 characters.').optional();
// City — HaloPSA's line3 (the slot Halo calls City). Bound matches HaloSiteRequestValidator's City (100).
// This optional variant is only for a block that is not on screen (role-hidden profile section);
// every visible address form validates against requiredCitySchema below.
export const addressLine3Schema = z.string().max(100, 'City must not exceed 100 characters.').optional();
// Free-text State/Region field (HaloPSA's line4) — always optional.
export const addressStateSchema = z.string().max(100, 'State must not exceed 100 characters.').optional();
export const postalCodeSchema = z.string().max(20, 'Postal code must not exceed 20 characters.').optional();

// Required variants — every form that always renders Address Line 1/Postal Code (the checkout
// page, the billing dialog, and the profile section while its billing/shipping block is visible)
// validates against these instead. Address Line 2 has no required variant; it stays optional
// everywhere. City is required like Address Line 1 (mirrors the backend validators).
export const requiredAddressLine1Schema = z
  .string()
  .min(1, 'Address line 1 is required.')
  .max(255, 'Address line 1 must not exceed 255 characters.');
export const requiredCitySchema = z
  .string()
  .min(1, 'City is required.')
  .max(100, 'City must not exceed 100 characters.');
export const requiredPostalCodeSchema = z
  .string()
  .min(1, 'Postal code is required.')
  .max(20, 'Postal code must not exceed 20 characters.');

/**
 * One address block's schema. `showLine3` adds the free-text City field (line3) (shipping only —
 * HaloPSA's billing/invoice address doesn't have one). Country/region are included as generic
 * id+name pairs; a form that shares its country across two addresses (billing + shipping) validates
 * the shared country field once at the top level instead of duplicating it into each block.
 */
export function createAddressBlockSchema({ showLine3 = false, includeCountryRegion = true } = {}) {
  const shape = {
    address1: addressLine1Schema,
    address2: addressLine2Schema,
    postalCode: postalCodeSchema,
  };

  if (showLine3) {
    shape.address3 = addressLine3Schema;
    shape.state = addressStateSchema;
  } else if (includeCountryRegion) {
    shape.country = z.string().optional();
    shape.countryId = z.union([z.string(), z.number()]).optional();
    shape.region = z.string().optional();
    shape.regionId = z.union([z.string(), z.number()]).optional();
  }

  return z.object(shape);
}
